import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import ReactDOM from 'react-dom';

// Edge effect container
const EdgeEffectContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  height: 100vh;
  overflow: visible;
  pointer-events: none;
  z-index: 9999;
`;

// Bookmark particle - reduced size
const BookmarkParticle = styled(motion.div)`
  position: absolute;
  width: 22px;
  height: 26px;
  color: #4285F4;
  opacity: 0;
  transform-origin: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  filter: drop-shadow(0 0 4px rgba(66, 133, 244, 0.7));
`;

// Subtle edge glow for left swipe - reduced intensity
const EdgeGlow = styled(motion.div)<{ $direction: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  ${props => props.$direction === 'left' ? 'left: 0;' : 'right: 0;'}
  width: 4px;
  height: 100%;
  background: ${props => props.$direction === 'left' 
    ? 'linear-gradient(to right, rgba(180, 180, 180, 0.7), rgba(180, 180, 180, 0))' 
    : 'linear-gradient(to left, rgba(66, 133, 244, 0.7), rgba(66, 133, 244, 0))'};
  opacity: 0;
`;

interface SwipeEffectProps {
  isVisible: boolean;
  direction: 'left' | 'right';
  id?: string;
}

// Create a portal component to render directly to body
const SwipeEffectPortal: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  
  useEffect(() => {
    const node = document.createElement('div');
    node.style.position = 'fixed';
    node.style.top = '0';
    node.style.left = '0';
    node.style.width = '0';
    node.style.height = '0';
    node.style.overflow = 'visible';
    node.style.pointerEvents = 'none';
    
    document.body.appendChild(node);
    setPortalNode(node);
    
    return () => {
      document.body.removeChild(node);
    };
  }, []);
  
  return portalNode ? ReactDOM.createPortal(children, portalNode) : null;
};

const SwipeEffect: React.FC<SwipeEffectProps> = ({ isVisible, direction, id }) => {
  // Generate bookmark particles with random distribution
  const [bookmarkParticles, setBookmarkParticles] = useState<Array<{
    id: number;
    startY: number;
    endY: number; 
    startX: number;
    endX: number;
    scale: number;
    rotation: number;
    delay: number;
    duration: number;
  }>>([]);

  useEffect(() => {
    if (isVisible) {
      if (direction === 'right') {
        // Create randomized particles with natural distribution
        const numberOfParticles = 10; // Slightly reduced for cleaner effect
        const windowHeight = window.innerHeight;
        
        const newParticles = Array.from({ length: numberOfParticles }, (_, i) => {
          // Distribute particles along screen height with slight clustering near center
          const verticalPosition = Math.random();
          const startY = windowHeight * (0.3 + 0.4 * verticalPosition); // Concentrate more in the middle
          
          // Create natural arcs rather than straight lines
          // Particles now move in slight arcs simulating air resistance
          const endY = startY + (Math.random() * 100 - 50) - 40; // Slight upward bias
          
          // Horizontal movement with acceleration pattern
          const startX = Math.random() * -5; // Start closer to edge
          
          // Vary distance to create depth perception
          const distance = 15 + Math.random() * 50;
          const endX = distance;
          
          // Randomize scale to create depth
          const scale = 0.6 + Math.random() * 0.5;
          
          // Rotation follows natural physics - objects tend to rotate in direction of movement
          const rotation = Math.random() > 0.5 ? 
            30 + Math.random() * 90 : // Clockwise
            -30 - Math.random() * 90; // Counter-clockwise
          
          // Stagger effect with slight delay variation - reduced max delay
          const delay = Math.random() * 0.1; // Reduced from 0.15 to 0.1
          
          // Fixed shorter duration for all particles
          const duration = 0.65; // Fixed duration instead of random
          
          return {
            id: i,
            startY,
            endY,
            startX,
            endX,
            scale,
            rotation,
            delay,
            duration
          };
        });
        
        setBookmarkParticles(newParticles);
      }
    } else {
      setBookmarkParticles([]);
    }
  }, [isVisible, direction]);

  return (
    <SwipeEffectPortal>
      <AnimatePresence>
        {isVisible && (
          <EdgeEffectContainer
            style={{ 
              [direction === 'left' ? 'left' : 'right']: 0,
              width: direction === 'right' ? '100px' : '20px',
            }}
          >
            {/* For left swipe, show a subtle edge glow */}
            {direction === 'left' && (
              <EdgeGlow 
                $direction="left"
                initial={{ opacity: 0, width: 0 }}
                animate={{ 
                  opacity: [0, 0.7, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1, 0], 
                  width: [0, 10, 10, 9, 8, 7, 5, 3, 2, 0]
                }}
                transition={{ 
                  duration: 0.65, // Reduced from 0.8 to match particle duration
                  ease: "easeOut",
                  times: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.9, 1]
                }}
              />
            )}

            {/* For right swipe, show animated bookmark particles with improved physics */}
            {direction === 'right' && (
              <>
                <EdgeGlow 
                  $direction="right"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ 
                    opacity: [0, 0.8, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1, 0], 
                    width: [0, 12, 12, 10, 9, 8, 6, 5, 3, 2, 0]
                  }}
                  transition={{ 
                    duration: 0.65, // Reduced from 0.8 to match particle duration
                    ease: "easeOut", 
                    times: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
                  }}
                />
                
                {bookmarkParticles.map(particle => (
                  <BookmarkParticle
                    key={particle.id}
                    style={{
                      top: particle.startY,
                      right: 0,
                    }}
                    initial={{ 
                      opacity: 0,
                      x: -particle.startX,
                      y: 0,
                      scale: 0.2,
                      rotate: 0
                    }}
                    animate={{ 
                      opacity: [0, 0.9, 0.9, 0.7, 0.5, 0.2, 0], 
                      // Natural physics-based movement
                      // Initial fast movement then slowing down (easing out)
                      x: [
                        -particle.startX, 
                        -particle.endX * 0.4, 
                        -particle.endX * 0.65, 
                        -particle.endX * 0.8, 
                        -particle.endX * 0.9, 
                        -particle.endX
                      ],
                      // Create subtle arc with upward bias at beginning then gradual fall
                      y: [
                        0, 
                        (particle.endY - particle.startY) * 0.1 - 5, 
                        (particle.endY - particle.startY) * 0.3 - 8, 
                        (particle.endY - particle.startY) * 0.6 - 5, 
                        (particle.endY - particle.startY) * 0.8, 
                        particle.endY - particle.startY
                      ],
                      // Natural scale increase then decrease
                      scale: [0.2, particle.scale * 0.8, particle.scale, particle.scale * 0.9, particle.scale * 0.7, particle.scale * 0.5, 0.2],
                      // Gradual rotation as it moves
                      rotate: [0, particle.rotation * 0.2, particle.rotation * 0.5, particle.rotation * 0.8, particle.rotation, particle.rotation * 1.1]
                    }}
                    transition={{
                      duration: particle.duration,
                      ease: "easeOut",
                      delay: particle.delay,
                      times: [0, 0.15, 0.3, 0.5, 0.7, 1]
                    }}
                  >
                    <i className="fi fi-rr-bookmark" />
                  </BookmarkParticle>
                ))}
              </>
            )}
          </EdgeEffectContainer>
        )}
      </AnimatePresence>
    </SwipeEffectPortal>
  );
};

export default SwipeEffect;